import { createTheme, Theme } from '@mui/material/styles';
import { DashboardTheme } from '../types';

// Create MUI theme from dashboard theme
export const createMuiTheme = (dashboardTheme: DashboardTheme): Theme => {
  const mode = dashboardTheme.mode;
  
  return createTheme({
    palette: {
      mode,
      primary: {
        main: dashboardTheme.primaryColor,
      },
      secondary: {
        main: dashboardTheme.secondaryColor,
      },
      background: {
        default: dashboardTheme.backgroundColor,
        paper: dashboardTheme.cardBackground,
      },
      text: {
        primary: dashboardTheme.textColor,
      },
      // Custom colors using theme augmentation
      ...(mode === 'light' ? {
        grey: {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
        },
      } : {
        grey: {
          50: '#fafafa',
          100: '#f5f5f5',
          200: '#eeeeee',
          300: '#e0e0e0',
          400: '#bdbdbd',
          500: '#9e9e9e',
          600: '#757575',
          700: '#616161',
          800: '#424242',
          900: '#212121',
        },
      }),
    },
    shape: {
      borderRadius: dashboardTheme.borderRadius,
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 600,
      },
      h2: {
        fontWeight: 600,
      },
      h3: {
        fontWeight: 600,
      },
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
      button: {
        textTransform: 'none',
        fontWeight: 500,
      },
    },
    components: {
      // Override component styles based on dashboard theme
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: dashboardTheme.cardBackground,
            transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: dashboardTheme.cardBackground,
            color: dashboardTheme.textColor,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: dashboardTheme.cardBackground,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: dashboardTheme.borderRadius,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: dashboardTheme.borderRadius,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: dashboardTheme.borderRadius / 2,
          },
        },
      },
    },
  });
};

// Helper function to lighten a color
export const lightenColor = (color: string, amount: number): string => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) + amt;
  const G = ((num >> 8) & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
    (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
    (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

// Helper function to darken a color
export const darkenColor = (color: string, amount: number): string => {
  const num = parseInt(color.replace('#', ''), 16);
  const amt = Math.round(2.55 * amount);
  const R = (num >> 16) - amt;
  const G = ((num >> 8) & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return '#' + (0x1000000 + (R > 255 ? 255 : R < 0 ? 0 : R) * 0x10000 +
    (G > 255 ? 255 : G < 0 ? 0 : G) * 0x100 +
    (B > 255 ? 255 : B < 0 ? 0 : B)).toString(16).slice(1);
};

// Helper function to get contrast color (black or white)
export const getContrastColor = (color: string): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
};

// Helper function to validate hex color
export const isValidHexColor = (color: string): boolean => {
  const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexColorRegex.test(color);
};

// Generate color palette variations
export const generateColorPalette = (baseColor: string) => {
  if (!isValidHexColor(baseColor)) {
    return {
      50: '#f5f5f5',
      100: '#eeeeee',
      200: '#e0e0e0',
      300: '#bdbdbd',
      400: '#9e9e9e',
      500: baseColor,
      600: baseColor,
      700: baseColor,
      800: baseColor,
      900: baseColor,
    };
  }

  return {
    50: lightenColor(baseColor, 45),
    100: lightenColor(baseColor, 35),
    200: lightenColor(baseColor, 25),
    300: lightenColor(baseColor, 15),
    400: lightenColor(baseColor, 8),
    500: baseColor,
    600: darkenColor(baseColor, 8),
    700: darkenColor(baseColor, 15),
    800: darkenColor(baseColor, 25),
    900: darkenColor(baseColor, 35),
  };
};

// Predefined theme presets
export const themePresets = [
  {
    id: 'default-light',
    name: 'Default Light',
    description: 'Clean and modern light theme',
    theme: {
      mode: 'light' as const,
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      backgroundColor: '#f5f5f5',
      textColor: '#333333',
      cardBackground: '#ffffff',
      borderRadius: 8,
      customColors: {
        accent1: '#ff9800',
        accent2: '#4caf50'
      }
    }
  },
  {
    id: 'default-dark',
    name: 'Default Dark',
    description: 'Sleek dark theme for low-light environments',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#90caf9',
      secondaryColor: '#f48fb1',
      backgroundColor: '#121212',
      textColor: '#ffffff',
      cardBackground: '#1e1e1e',
      borderRadius: 8,
      customColors: {
        accent1: '#ffb74d',
        accent2: '#81c784'
      }
    }
  },
  {
    id: 'ocean-breeze',
    name: 'Ocean Breeze',
    description: 'Refreshing aqua blues with gentle waves',
    theme: {
      mode: 'light' as const,
      primaryColor: '#00bcd4',
      secondaryColor: '#0097a7',
      backgroundColor: '#e0f7fa',
      textColor: '#006064',
      cardBackground: '#ffffff',
      borderRadius: 16,
      customColors: {
        accent1: '#26c6da',
        accent2: '#4dd0e1'
      }
    }
  },
  {
    id: 'sunset-glow',
    name: 'Sunset Glow',
    description: 'Warm gradients of orange and pink twilight',
    theme: {
      mode: 'light' as const,
      primaryColor: '#ff6b35',
      secondaryColor: '#ff8a65',
      backgroundColor: '#fff8f5',
      textColor: '#3e2723',
      cardBackground: '#ffffff',
      borderRadius: 20,
      customColors: {
        accent1: '#ffab40',
        accent2: '#ff7043'
      }
    }
  },
  {
    id: 'forest-mystique',
    name: 'Forest Mystique',
    description: 'Deep emerald greens with natural harmony',
    theme: {
      mode: 'light' as const,
      primaryColor: '#2e7d32',
      secondaryColor: '#388e3c',
      backgroundColor: '#e8f5e8',
      textColor: '#1b5e20',
      cardBackground: '#ffffff',
      borderRadius: 12,
      customColors: {
        accent1: '#66bb6a',
        accent2: '#4caf50'
      }
    }
  },
  {
    id: 'lavender-dreams',
    name: 'Lavender Dreams',
    description: 'Soft purples with elegant sophistication',
    theme: {
      mode: 'light' as const,
      primaryColor: '#7b1fa2',
      secondaryColor: '#8e24aa',
      backgroundColor: '#f3e5f5',
      textColor: '#4a148c',
      cardBackground: '#ffffff',
      borderRadius: 18,
      customColors: {
        accent1: '#ab47bc',
        accent2: '#ba68c8'
      }
    }
  },
  {
    id: 'arctic-frost',
    name: 'Arctic Frost',
    description: 'Cool blues and whites like pristine snow',
    theme: {
      mode: 'light' as const,
      primaryColor: '#1565c0',
      secondaryColor: '#1976d2',
      backgroundColor: '#f1f8ff',
      textColor: '#0d47a1',
      cardBackground: '#ffffff',
      borderRadius: 14,
      customColors: {
        accent1: '#42a5f5',
        accent2: '#64b5f6'
      }
    }
  },
  {
    id: 'rose-garden',
    name: 'Rose Garden',
    description: 'Romantic pinks and roses in bloom',
    theme: {
      mode: 'light' as const,
      primaryColor: '#c2185b',
      secondaryColor: '#d81b60',
      backgroundColor: '#fce4ec',
      textColor: '#880e4f',
      cardBackground: '#ffffff',
      borderRadius: 22,
      customColors: {
        accent1: '#e91e63',
        accent2: '#f06292'
      }
    }
  },
  {
    id: 'golden-hour',
    name: 'Golden Hour',
    description: 'Warm golds and ambers of perfect lighting',
    theme: {
      mode: 'light' as const,
      primaryColor: '#f57c00',
      secondaryColor: '#ff8f00',
      backgroundColor: '#fff8e1',
      textColor: '#e65100',
      cardBackground: '#ffffff',
      borderRadius: 10,
      customColors: {
        accent1: '#ffb300',
        accent2: '#ffc107'
      }
    }
  },
  {
    id: 'midnight-elegance',
    name: 'Midnight Elegance',
    description: 'Sophisticated dark blues with silver accents',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#3f51b5',
      secondaryColor: '#5c6bc0',
      backgroundColor: '#0a0e1a',
      textColor: '#c5cae9',
      cardBackground: '#1a1d29',
      borderRadius: 16,
      customColors: {
        accent1: '#7986cb',
        accent2: '#9fa8da'
      }
    }
  },
  {
    id: 'cyber-neon',
    name: 'Cyber Neon',
    description: 'Electric greens and cyans for futuristic vibes',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#00e676',
      secondaryColor: '#00bcd4',
      backgroundColor: '#0d1117',
      textColor: '#b9f6ca',
      cardBackground: '#1c1f26',
      borderRadius: 8,
      customColors: {
        accent1: '#1de9b6',
        accent2: '#64ffda'
      }
    }
  },
  {
    id: 'crimson-shadow',
    name: 'Crimson Shadow',
    description: 'Deep reds with dramatic dark contrasts',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#d32f2f',
      secondaryColor: '#f44336',
      backgroundColor: '#1a0a0a',
      textColor: '#ffcdd2',
      cardBackground: '#2d1a1a',
      borderRadius: 12,
      customColors: {
        accent1: '#ef5350',
        accent2: '#e57373'
      }
    }
  },
  {
    id: 'aurora-borealis',
    name: 'Aurora Borealis',
    description: 'Mystical greens and blues like northern lights',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#00897b',
      secondaryColor: '#26a69a',
      backgroundColor: '#0f1419',
      textColor: '#b2dfdb',
      cardBackground: '#1e2428',
      borderRadius: 20,
      customColors: {
        accent1: '#4db6ac',
        accent2: '#80cbc4'
      }
    }
  },
  {
    id: 'cosmic-purple',
    name: 'Cosmic Purple',
    description: 'Deep space purples with stellar beauty',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#673ab7',
      secondaryColor: '#7e57c2',
      backgroundColor: '#12091a',
      textColor: '#d1c4e9',
      cardBackground: '#1f1625',
      borderRadius: 18,
      customColors: {
        accent1: '#9575cd',
        accent2: '#b39ddb'
      }
    }
  },
  {
    id: 'ember-flame',
    name: 'Ember Flame',
    description: 'Fiery oranges and reds with warmth',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#ff5722',
      secondaryColor: '#ff7043',
      backgroundColor: '#1a0f0a',
      textColor: '#ffccbc',
      cardBackground: '#2d1f1a',
      borderRadius: 14,
      customColors: {
        accent1: '#ff8a65',
        accent2: '#ffab91'
      }
    }
  },
  {
    id: 'tropical-paradise',
    name: 'Tropical Paradise',
    description: 'Vibrant teals and corals of tropical waters',
    theme: {
      mode: 'light' as const,
      primaryColor: '#00acc1',
      secondaryColor: '#ff7043',
      backgroundColor: '#e0f2f1',
      textColor: '#004d40',
      cardBackground: '#ffffff',
      borderRadius: 24,
      customColors: {
        accent1: '#26c6da',
        accent2: '#ffab40'
      }
    }
  },
  {
    id: 'winter-wonderland',
    name: 'Winter Wonderland',
    description: 'Cool silvers and blues of winter mornings',
    theme: {
      mode: 'light' as const,
      primaryColor: '#607d8b',
      secondaryColor: '#78909c',
      backgroundColor: '#eceff1',
      textColor: '#263238',
      cardBackground: '#ffffff',
      borderRadius: 8,
      customColors: {
        accent1: '#90a4ae',
        accent2: '#b0bec5'
      }
    }
  },
  {
    id: 'cherry-blossom',
    name: 'Cherry Blossom',
    description: 'Delicate pinks inspired by Japanese sakura',
    theme: {
      mode: 'light' as const,
      primaryColor: '#e91e63',
      secondaryColor: '#ad1457',
      backgroundColor: '#fdf2f8',
      textColor: '#880e4f',
      cardBackground: '#ffffff',
      borderRadius: 16,
      customColors: {
        accent1: '#f06292',
        accent2: '#f8bbd9'
      }
    }
  },
  {
    id: 'deep-ocean',
    name: 'Deep Ocean',
    description: 'Mysterious dark blues of ocean depths',
    theme: {
      mode: 'dark' as const,
      primaryColor: '#0288d1',
      secondaryColor: '#0277bd',
      backgroundColor: '#0a1929',
      textColor: '#b3e5fc',
      cardBackground: '#1e293b',
      borderRadius: 12,
      customColors: {
        accent1: '#03a9f4',
        accent2: '#29b6f6'
      }
    }
  },
  {
    id: 'sunset-beach',
    name: 'Sunset Beach',
    description: 'Warm beach sunset with golden sands',
    theme: {
      mode: 'light' as const,
      primaryColor: '#ff9800',
      secondaryColor: '#f57c00',
      backgroundColor: '#fff3c4',
      textColor: '#e65100',
      cardBackground: '#ffffff',
      borderRadius: 20,
      customColors: {
        accent1: '#ffb74d',
        accent2: '#ffcc02'
      }
    }
  }
];

// Get theme preset by ID
export const getThemePreset = (id: string) => {
  return themePresets.find(preset => preset.id === id);
};

// Export theme to JSON
export const exportTheme = (theme: DashboardTheme): string => {
  return JSON.stringify(theme, null, 2);
};

// Import theme from JSON
export const importTheme = (themeJson: string): DashboardTheme | null => {
  try {
    const parsed = JSON.parse(themeJson);
    
    // Validate theme structure
    if (!parsed.mode || !parsed.primaryColor || !parsed.secondaryColor || 
        !parsed.backgroundColor || !parsed.textColor || !parsed.cardBackground) {
      return null;
    }
    
    return parsed as DashboardTheme;
  } catch (error) {
    return null;
  }
};